import { Component, ErrorInfo, ReactNode } from "react";
import { AlertCircle, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="h-screen w-screen flex items-center justify-center p-6 bg-background">
          <div className="max-w-md w-full space-y-4">
            <Alert variant="destructive" className="border-2">
              <AlertCircle className="h-5 w-5" />
              <AlertTitle className="text-lg font-bold">应用发生错误</AlertTitle>
              <AlertDescription className="mt-2 text-sm opacity-90 break-words">
                {this.state.error?.message || "发生了一个意外错误，导致应用无法继续运行。"}
              </AlertDescription>
            </Alert>
            <div className="flex flex-col gap-2">
              <Button 
                onClick={this.handleReset}
                className="w-full gap-2"
              >
                <RefreshCcw className="w-4 h-4" />
                重新加载应用
              </Button>
              <Button 
                variant="outline" 
                onClick={() => this.setState({ hasError: false, error: null })}
                className="w-full"
              >
                尝试恢复
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground text-center">
              如果问题持续存在，请检查 USB 连接或清除浏览器缓存。
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
